import React, { useState, useEffect, useRef } from 'react'
import './style/Checkout.css'
import { useNavigate } from 'react-router-dom'
import { collection, getDocs, doc, getDoc } from 'firebase/firestore'
import HeroImg from '../resources/fotosKathi/18.jpg'
import { db, functions } from '../firebaseConfig'
import { httpsCallable } from 'firebase/functions'
import AbwesendBanner from '../components/AbwesendBanner'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const Checkout = () => {
  const navigate = useNavigate()
  const [shippingOrPickup, setShippingOrPickup] = useState('shipping')
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    ...(shippingOrPickup === 'shipping' && {
      address: '',
      city: '',
      postalCode: '',
      country: ''
    })
  })

  const [cartItems, setCartItems] = useState([])
  const [totalPrice, setTotalPrice] = useState(0)
  const [orderConfirmation, setOrderConfirmation] = useState(false)
  const [orderWeight, setOrderWeight] = useState(0)
  const [shippingCost, setShippingCost] = useState(4.99)
  const grandTotal =
    totalPrice + (shippingOrPickup === 'shipping' ? shippingCost : 0)

  const [shippingRules, setShippingRules] = useState([])
  const [shopClosed, setShopClosed] = useState(false)
  const [closedMessage, setClosedMessage] = useState('')
  const [absenceChecked, setAbsenceChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [emailNote, setEmailNote] = useState('')
  const [confirmedTotal, setConfirmedTotal] = useState(null)
  const requestRef = useRef({ key: '', id: '' })

  useEffect(() => {
    const fetchShippingRules = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'shippingRules'))
        const fetchedRules = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))

        // Sortiere die Regeln nach Gewicht und speichere sie im State
        setShippingRules(fetchedRules.sort((a, b) => a.weight - b.weight))
        console.log('Versandregeln:', fetchedRules)
      } catch (error) {
        console.error('Fehler beim Abrufen der Versandregeln:', error)
      }
    }

    fetchShippingRules()
  }, [])

  useEffect(() => {
    let active = true
    const loadAbsence = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'abwesenheit'))
        if (!active) return
        if (snap.exists() && snap.data().abwesenheit === true) {
          setShopClosed(true)
          setClosedMessage(snap.data().nachricht || '')
        }
      } catch (error) {
        console.error('Abwesenheit konnte nicht geladen werden:', error)
      } finally {
        if (active) setAbsenceChecked(true)
      }
    }
    loadAbsence()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!cartItems || cartItems.length === 0 || shippingRules.length === 0)
      return // Sicherstellen, dass cartItems und shippingRules existieren

    let totalWeight = 0

    cartItems.forEach(cartItem => {
      const { selectedOptions } = cartItem
      if (selectedOptions) {
        const menge = parseFloat(selectedOptions.menge) || 0
        const stueckzahl = parseInt(selectedOptions.stueckzahl, 10) || 0

        totalWeight += menge * stueckzahl
      }
    })

    // Standardversandkosten (falls keine passende Regel gefunden wird)
    let cost = 4.99

    // Regeln durchgehen und passende Versandkosten setzen
    for (let rule of shippingRules) {
      if (totalWeight >= rule.weight) {
        cost = rule.cost
      }
    }

    setShippingCost(cost)
    setOrderWeight(totalWeight)
    console.log('Gesamtgewicht:', totalWeight)
    console.log('Versandkosten:', cost)
  }, [cartItems, shippingRules])

  useEffect(() => {
    const fetchCartItems = async () => {
      const storedCart = JSON.parse(localStorage.getItem('cart')) || []

      if (storedCart.length === 0) {
        setCartItems([])
        setTotalPrice(0)
        return
      }

      try {
        const productSnapshot = await getDocs(collection(db, 'products')) // Passe ggf. den Collection-Namen an
        const validProducts = productSnapshot.docs
          .filter(doc => doc.data().public) // Nur öffentliche Produkte
          .map(doc => ({ id: doc.id, ...doc.data() }))

        console.log('Valid Products:', validProducts)

        const validProductIds = validProducts.map(product => product.id)

        const validItems = storedCart.filter(item =>
          validProductIds.includes(item.id)
        )

        if (validItems.length < storedCart.length) {
          alert(
            'Einige Produkte in deinem Warenkorb sind nicht mehr verfügbar und wurden entfernt.'
          )
          localStorage.setItem('cart', JSON.stringify(validItems))
        }

        setCartItems(validItems)

        const total = validItems.reduce((acc, item) => {
          const mengeInGramm = parseInt(
            item.selectedOptions.menge.replace('g', ''),
            10
          )
          let aufpreis = 0
          if (mengeInGramm === 250) aufpreis = 1.5
          else if (mengeInGramm === 500) aufpreis = 1.0

          const einzelpreis = (item.price / 1000) * mengeInGramm + aufpreis
          return acc + einzelpreis * item.selectedOptions.stueckzahl
        }, 0)

        setTotalPrice(total)
      } catch (error) {
        console.error('Fehler beim Überprüfen der Produkte:', error)
      }
    }

    fetchCartItems()
  }, [])

  const calculateItemTotal = item => {
    const menge = parseInt(item.selectedOptions.menge.replace('g', ''), 10)
    let aufpreis = 0
    if (menge === 250) aufpreis = 1.5
    else if (menge === 500) aufpreis = 1.0

    const einzelpreis = (item.price / 1000) * menge + aufpreis
    return (einzelpreis * item.selectedOptions.stueckzahl).toFixed(2)
  }

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const chooseFulfillment = mode => {
    setShippingOrPickup(mode)
    setFormData(prev => {
      const next = {
        firstName: prev.firstName,
        lastName: prev.lastName,
        email: prev.email
      }
      if (mode === 'shipping') {
        next.address = prev.address || ''
        next.city = prev.city || ''
        next.postalCode = prev.postalCode || ''
        next.country = prev.country || ''
      }
      return next
    })
  }

  const requestIdFor = key => {
    if (requestRef.current.key === key) return requestRef.current.id
    let id = ''
    try {
      const stored = JSON.parse(sessionStorage.getItem('checkoutRequest') || 'null')
      if (stored && stored.key === key && typeof stored.id === 'string') {
        id = stored.id
      }
    } catch (error) {
      id = ''
    }
    if (!id) id = crypto.randomUUID()
    requestRef.current = { key, id }
    try {
      sessionStorage.setItem('checkoutRequest', JSON.stringify({ key, id }))
    } catch (error) {
      // Privater Modus kann sessionStorage sperren. Die ID bleibt dann nur im Speicher.
    }
    return id
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (submitting) return
    if (!absenceChecked || shopClosed) {
      alert('Der Shop ist zurzeit geschlossen. Bitte versuche es später erneut.')
      return
    }
    if (cartItems.length === 0) {
      alert('Ihr Warenkorb ist leer.')
      return
    }
    if (
      cartItems.some(item => {
        const qty = Math.round(Number(item?.selectedOptions?.stueckzahl))
        return (
          !item?.id ||
          !item.selectedOptions?.menge ||
          !Number.isInteger(qty) ||
          qty < 1 ||
          qty > 99
        )
      })
    ) {
      alert('Der Warenkorb enthält einen ungültigen Artikel. Bitte lege ihn erneut hinein.')
      return
    }

    const customer = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email
    }
    if (shippingOrPickup === 'shipping') {
      customer.address = formData.address
      customer.city = formData.city
      customer.postalCode = formData.postalCode
      customer.country = formData.country
    }

    const items = cartItems.map(item => {
      const selected = {
        productId: item.id,
        menge: item.selectedOptions.menge,
        stueckzahl: Math.round(Number(item.selectedOptions.stueckzahl)),
        mahlgrad: item.selectedOptions.mahlgrad
      }
      if (item.selectedOptions.mahlgrad === 'gemahlen') {
        selected.kaffeeart = item.selectedOptions.kaffeeart
      }
      return selected
    })

    const requestKey = JSON.stringify({
      fulfillment: shippingOrPickup,
      customer,
      items
    })
    const placeOrder = httpsCallable(functions, 'placeOrder')
    setSubmitting(true)

    try {
      const result = await placeOrder({
        requestId: requestIdFor(requestKey),
        fulfillment: shippingOrPickup,
        customer,
        items
      })
      localStorage.removeItem('cart')
      try {
        sessionStorage.removeItem('checkoutRequest')
      } catch (error) {
        // sessionStorage ist hier nur ein Schutz gegen Doppelklicks.
      }
      setConfirmedTotal(result.data.total)
      setEmailNote(
        result.data.emailSent
          ? ''
          : 'Die Bestätigung per E-Mail konnte nicht gesendet werden. Deine Bestellung ist trotzdem bei uns eingegangen.'
      )
      setOrderConfirmation(true)
    } catch (error) {
      console.error('Bestellung fehlgeschlagen:', error)
      const message = typeof error?.message === 'string' ? error.message : ''
      if (message && !message.includes('internal')) {
        alert(message)
      } else {
        alert('Ein Fehler ist aufgetreten. Bitte versuche es erneut oder kontaktiere uns.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ backgroundColor: '#fcfbf7' }}>
      <div
        className='aktuellesPageImageDiv'
        style={{ backgroundImage: `url(${HeroImg})` }}
      >
        <div className='textDivAktuellesPage'>
          <h1>Checkout</h1>
          <p>Wir freuen uns über Ihre Bestellung!</p>
        </div>
      </div>
      <div className='checkout-container'>
        {shopClosed && <AbwesendBanner message={closedMessage} />}
        {orderConfirmation && (
          <div className='order-confirmation'>
            <div className='order-confirmation-card'>
              <CheckCircleOutlineIcon className='order-confirmation-icon' />
              <h2>Vielen Dank für Ihre Bestellung!</h2>
              {typeof confirmedTotal === 'number' && (
                <p className='order-confirmation-total'>
                  Gesamtsumme: {confirmedTotal.toFixed(2)} €
                </p>
              )}
              {emailNote && <p className='order-confirmation-note'>{emailNote}</p>}
              <button onClick={() => navigate('/')}>
                Zurück zur Startseite
              </button>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className='checkout-form'>
          <div className='customer-info'>
            <h2>Kundendaten</h2>
            <label>Vorname:</label>
            <input
              type='text'
              name='firstName'
              value={formData.firstName}
              onChange={handleChange}
              required
            />

            <label>Nachname:</label>
            <input
              type='text'
              name='lastName'
              value={formData.lastName}
              onChange={handleChange}
              required
            />

            <label>Email:</label>
            <input
              type='email'
              name='email'
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className='shipping-or-pickup-button'>
            <h2>Versandart</h2>
            <div className='fulfillment-options'>
              <button
                type='button'
                onClick={() => chooseFulfillment('pickup')}
                className={`checkout-button-shipping ${
                  shippingOrPickup === 'pickup' ? 'active' : ''
                }`}
              >
                <StorefrontOutlinedIcon />
                <span>Abholung</span>
              </button>
              <button
                type='button'
                className={`checkout-button-shipping ${
                  shippingOrPickup === 'shipping' ? 'active' : ''
                }`}
                onClick={() => chooseFulfillment('shipping')}
              >
                <LocalShippingOutlinedIcon />
                <span>Versand</span>
              </button>
            </div>
          </div>

          {shippingOrPickup === 'shipping' ? (
            <div className='shipping-info'>
              <h2>Lieferadresse</h2>
              <label>Adresse:</label>
              <input
                type='text'
                name='address'
                value={formData.address}
                onChange={handleChange}
                required
              />

              <label>Stadt:</label>
              <input
                type='text'
                name='city'
                value={formData.city}
                onChange={handleChange}
                required
              />

              <label>PLZ:</label>
              <input
                type='text'
                name='postalCode'
                value={formData.postalCode}
                onChange={handleChange}
                required
              />

              <label>Land:</label>
              <input
                type='text'
                name='country'
                value={formData.country}
                onChange={handleChange}
                required
              />
            </div>
          ) : (
            <div className='shipping-info'>
              <h2>Abholadresse</h2>
              <p className='pickup-address'>
                Zwingler Kaffee
                <br />
                Innviertlerstraße 19
                <br />
                4911 Tumeltsham
              </p>
              <p className='pickup-note'>
                Abholung ist nur nach Terminvereinbarung möglich. Du bekommst
                eine E-Mail mit weiteren Informationen.
              </p>
            </div>
          )}

          <button
            type='submit'
            className='checkout-button'
            disabled={submitting || shopClosed || !absenceChecked}
          >
            {shopClosed
              ? 'Zurzeit geschlossen'
              : submitting
                ? 'Wird gesendet...'
                : 'Bestellung abschicken'}
          </button>
        </form>
        <div className='order-summary'>
          <h2>Bestellübersicht</h2>
          <ul>
            {cartItems.map((item, index) => (
              <li key={index} className='order-item'>
                <img
                  src={item.images[0]}
                  alt={item.name}
                  className='order-item-image'
                />
                <div className='order-item-details'>
                  <h3>{item.name}</h3>
                  <p>
                    {item.selectedOptions.stueckzahl}x{' '}
                    {item.selectedOptions.menge}
                  </p>
                  <p>{calculateItemTotal(item)} €</p>
                </div>
              </li>
            ))}
          </ul>
          <div className='order-summary-total'>
            <p>
              <span>Zwischensumme</span>
              <span>{totalPrice.toFixed(2)} €</span>
            </p>
            <p>
              {shippingOrPickup === 'shipping' ? (
                <>
                  <span>
                    Versand
                    {orderWeight
                      ? ` (${(orderWeight / 1000).toFixed(2)} kg)`
                      : ''}
                  </span>
                  <span>{shippingCost.toFixed(2)} €</span>
                </>
              ) : (
                <>
                  <span>Versand</span>
                  <span>Kostenfrei</span>
                </>
              )}
            </p>

            <h3>
              <span>Gesamt</span>
              <span>{grandTotal.toFixed(2)} €</span>
            </h3>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checkout
